import { useMemo } from 'react';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { DataTable, type DataTableMode, type DataTablePagination } from '@odo/shared-ui';
import type { IteroOrder } from '../../../../services/iteroBrowse';
import { BROWSE_TABLE_CARD_CLASS, SELECTED_ROW_CLASS, TABLE_ROW_HEIGHT } from './browserStyles';
import { buildOrderColumns } from './browseColumns';
import { formatPatientName } from './iteroBrowseFormat';
import { TableEmpty, TableError, TableSkeletonRows } from './TableStates';

const SKELETON_WIDTHS = ['w-20', 'w-32', 'w-24', 'w-24', 'w-20'];

interface IteroOrdersTableProps {
    rows: readonly IteroOrder[];
    isLoading: boolean;
    isUpdating: boolean;
    errorMessage: string | null;
    hasFilters: boolean;
    selectedRxId: string | null;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination | undefined;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    onSelect: (order: IteroOrder) => void;
    onClearFilters: () => void;
    onRetry: () => void;
}

export function IteroOrdersTable({
    rows,
    isLoading,
    isUpdating,
    errorMessage,
    hasFilters,
    selectedRxId,
    mode,
    onModeChange,
    pagination,
    hasMore,
    isLoadingMore,
    onLoadMore,
    onSelect,
    onClearFilters,
    onRetry
}: IteroOrdersTableProps) {
    const columns = useMemo(() => buildOrderColumns(), []);

    return (
        <DataTable
            label="iTero orders"
            columns={columns}
            rows={rows}
            getRowKey={(order) => order.rxId}
            rowAction={{
                onActivate: onSelect,
                getLabel: (order) =>
                    `Order ${order.orderCode}, ${formatPatientName(order.patientLastName, order.patientFirstName)}${order.rxId === selectedRxId ? ', selected' : ''}`
            }}
            rowClassName={(order) => (order.rxId === selectedRxId ? SELECTED_ROW_CLASS : undefined)}
            rowHeightClassName={TABLE_ROW_HEIGHT}
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={onLoadMore}
            loadMoreLabel="Load more orders"
            isLoading={isLoading}
            loadingRows={<TableSkeletonRows widths={SKELETON_WIDTHS} />}
            emptyState={
                errorMessage ? (
                    <TableError message={errorMessage} onRetry={onRetry} />
                ) : (
                    <TableEmpty
                        icon={DocumentMagnifyingGlassIcon}
                        message={
                            hasFilters
                                ? 'No orders match these filters.'
                                : 'No orders in the last 30 days.'
                        }
                        onClear={hasFilters ? onClearFilters : undefined}
                    />
                )
            }
            emptyStateCellClassName="px-3 py-16"
            className="min-h-0"
            cardClassName={BROWSE_TABLE_CARD_CLASS}
            scrollClassName={isUpdating ? 'opacity-60 transition-opacity' : 'transition-opacity'}
        />
    );
}
